/**
 * Contact Form Component
 * Form for creating and editing contacts
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContact, updateContact } from "../../services/contacts.service";
import {
  searchOrganizationOptions,
  organizationLabel,
} from "../../services/pickerOptions.service";
import { getEmailSuppression } from "../../services/emailSuppression.service";
import { useDebounce } from "../../hooks/useDebounce";
import { SUPPRESSION_REASON_LABELS } from "../../types/emailSuppression.types";
import { QUERY_KEYS } from "../../utils/constants";
import { US_STATES } from "../../types/event.types";
import type { Contact } from "../../types/contact.types";
import type { UUID } from "../../types/common.types";
import Button from "../common/Button";
import Input from "../common/Input";
import Select from "../common/Select";
import SearchableSelect from "../common/SearchableSelect";
import Textarea from "../common/Textarea";

interface ContactFormProps {
  contact?: Contact;
  organizationId?: UUID;
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * Inline warning shown beneath the Email input when the operator is
 * about to change the email to an address that's currently in the
 * suppression list. Informational only — does not block save. The
 * trigger on contacts re-derives the denormalized flag automatically
 * after save, so the new value's suppression state immediately becomes
 * visible on the contact record.
 *
 * Per audit V2 (2026-05-16) — clearing suppression by editing the
 * email is NOT a workflow we permit. The warning surfaces the
 * consequence; admin override (Phase 5) is the path for actually
 * unsuppressing an address.
 */
const EmailSuppressionWarning: React.FC<{
  email: string;
  originalEmail: string;
}> = ({ email, originalEmail }) => {
  const debouncedEmail = useDebounce(email, 400);
  const trimmed = debouncedEmail.trim().toLowerCase();
  const original = originalEmail.trim().toLowerCase();
  // Only check when value is a plausible email AND has actually changed
  // from the original (no warning while just editing the current value).
  const shouldCheck =
    trimmed !== "" &&
    trimmed !== original &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  const { data: suppression } = useQuery({
    queryKey: ["email-suppression-check", trimmed],
    queryFn: () => getEmailSuppression(trimmed),
    enabled: shouldCheck,
    staleTime: 60_000,
  });

  if (!shouldCheck || !suppression?.is_suppressed) {
    return null;
  }
  const label = suppression.reason
    ? (SUPPRESSION_REASON_LABELS[suppression.reason] ?? suppression.reason)
    : "suppressed";
  return (
    <p className="mt-1 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-md px-2 py-1">
      <strong>Heads up:</strong> {trimmed} is currently on the suppression list
      ({label}
      ). Campaign sends to this address will be blocked until a system admin
      removes the suppression.
    </p>
  );
};

const ContactForm: React.FC<ContactFormProps> = ({
  contact,
  organizationId: propOrgId,
  onSuccess,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!contact;

  // Form state
  const [formData, setFormData] = useState({
    first_name: contact?.first_name || "",
    last_name: contact?.last_name || "",
    email: contact?.email || "",
    phone_number: contact?.phone_number || "",
    title: contact?.title || "",
    description: contact?.description || "",
    organization_id: contact?.organization_id || propOrgId || "",
    address_line1: contact?.address_line1 || "",
    address_line2: contact?.address_line2 || "",
    city: contact?.city || "",
    state: contact?.state || "",
    postal_code: contact?.postal_code || "",
    country: contact?.country || "USA",
    date_of_birth: contact?.date_of_birth || "",
  });
  // SMS consent is recorded separately with the member conversation.
  const initialEmailOptedOut =
    contact?.email_opt_in === false || !!contact?.email_opt_out_at;
  const [doNotCall, setDoNotCall] = useState<boolean>(
    contact?.do_not_call ?? false,
  );
  const [doNotEmail, setDoNotEmail] = useState<boolean>(initialEmailOptedOut);
  const doNotContactAll = doNotCall && doNotEmail;
  const setAllChannels = (v: boolean) => {
    setDoNotCall(v);
    setDoNotEmail(v);
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  // Errors the SERVER raised. Client-side validation lives in `errors`; a rejected write used to
  // land only in the mutation's own state, which nothing rendered — so a refused save looked
  // exactly like a save that did nothing.
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Turn a Postgres refusal into something the person editing can act on.
   *
   * 23505 on the email index is the one that actually happens: contacts carry a UNIQUE index on
   * lower(email), so moving an address onto a contact when another contact already holds it is
   * refused. "Update failed" would leave the user re-clicking Update forever; naming the conflict
   * tells them the real task is to merge the two contacts.
   */
  const describeWriteFailure = (
    err: unknown,
  ): { field?: string; message: string } => {
    const e = err as { code?: string; message?: string; details?: string };
    const text = `${e?.message ?? ""} ${e?.details ?? ""}`;
    if (e?.code === "23505" && /email/i.test(text)) {
      return {
        field: "email",
        message:
          "Another contact already uses this email address. Emails must be unique, so this one " +
          "cannot be moved here — find the contact that already has it and merge the two records.",
      };
    }
    if (e?.code === "23505") {
      return {
        message:
          "That value is already used by another contact and must be unique.",
      };
    }
    if (e?.code === "42501" || e?.code === "PGRST301") {
      return { message: "You do not have permission to change this contact." };
    }
    if (e?.code === "PGRST116") {
      return {
        message:
          "This contact could not be updated — it may have been deleted or is no longer visible " +
          "to you. Reload the page and try again.",
      };
    }
    return {
      message:
        e?.message || "The contact could not be saved. Please try again.",
    };
  };

  const handleWriteFailure = (err: unknown) => {
    const { field, message } = describeWriteFailure(err);
    console.error("Contact save failed:", err);
    if (field) {
      setErrors((prev) => ({ ...prev, [field]: message }));
      setSubmitError(null);
    } else {
      setSubmitError(message);
    }
  };

  // Create contact mutation
  const createMutation = useMutation({
    mutationFn: createContact,
    // Contacts are never invited to log in from here: user invites happen only in
    // Settings > Users (owner decision 2026-10-16; the old checkbox sent an undefined role).
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACTS] });
      onSuccess?.();
    },
    onError: handleWriteFailure,
  });

  // Update contact mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: UUID; updates: Partial<Contact> }) =>
      updateContact(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTACTS] });
      onSuccess?.();
    },
    onError: handleWriteFailure,
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // First name validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    // Last name validation
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitError(null);
    if (!validate()) {
      return;
    }

    // Clean empty strings to undefined so Postgres doesn't reject "" for uuid/date columns
    const contactData: Partial<Contact> = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email || undefined,
      phone_number: formData.phone_number || undefined,
      title: formData.title || undefined,
      description: formData.description || undefined,
      // On edit, a cleared organization must be written as NULL: `undefined` drops the key from
      // the PATCH, so "No organization" used to save nothing. On create, omit it.
      organization_id: formData.organization_id
        ? formData.organization_id
        : isEditing
          ? (null as unknown as undefined)
          : undefined,
      address_line1: formData.address_line1 || undefined,
      address_line2: formData.address_line2 || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      postal_code: formData.postal_code || undefined,
      country: formData.country || undefined,
      date_of_birth: formData.date_of_birth || undefined,
      // Voice/"do-not-call" opt-out; the DB trigger stamps/clears do_not_call_at. Voice-only —
      // does NOT affect SMS or email (those are separate consent channels).
      do_not_call: doNotCall,
    };

    // Per-channel consent: write a channel's opt-out fields ONLY when it changed from the contact's
    // initial state, so we never disturb a value the user didn't touch (SMS and email are
    // independent; SMS is managed through the conversation-recording action).
    const nowIso = new Date().toISOString();
    if (doNotEmail !== initialEmailOptedOut) {
      contactData.email_opt_in = !doNotEmail;
      contactData.email_opt_out_at = doNotEmail ? nowIso : null;
    }

    if (isEditing && contact) {
      updateMutation.mutate({ id: contact.id, updates: contactData });
    } else {
      createMutation.mutate(contactData);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (submitError) {
      setSubmitError(null);
    }
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {submitError && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {submitError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="First Name"
          value={formData.first_name}
          onChange={(e) => handleChange("first_name", e.target.value)}
          error={errors.first_name}
          required
        />

        <Input
          label="Last Name"
          value={formData.last_name}
          onChange={(e) => handleChange("last_name", e.target.value)}
          error={errors.last_name}
          required
        />

        <div>
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            error={errors.email}
          />
          <EmailSuppressionWarning
            email={formData.email}
            originalEmail={contact?.email || ""}
          />
        </div>

        <Input
          label="Phone Number"
          type="tel"
          value={formData.phone_number}
          onChange={(e) => handleChange("phone_number", e.target.value)}
          error={errors.phone_number}
        />

        <fieldset className="space-y-2 rounded-md border border-gray-200 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Communication preferences
          </legend>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={doNotCall}
              onChange={(e) => setDoNotCall(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">
              Do not call <span className="text-gray-500">(voice)</span>
            </span>
          </label>
          <p className="text-xs text-gray-600">
            Manage SMS consent from the contact overview to record the member
            conversation.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={doNotEmail}
              onChange={(e) => setDoNotEmail(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">Do not email</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer border-t border-gray-100 pt-2">
            <input
              type="checkbox"
              checked={doNotContactAll}
              onChange={(e) => setAllChannels(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium">Do not call or email</span>
              <span className="block text-xs text-gray-500">
                Sets or clears voice and email preferences together. SMS consent
                is recorded separately.
              </span>
            </span>
          </label>
        </fieldset>

        <Input
          label="Title"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
        />

        <Input
          label="Date of Birth"
          type="date"
          value={formData.date_of_birth}
          onChange={(e) => handleChange("date_of_birth", e.target.value)}
        />
      </div>

      {/* Server-side search: PROD has ~2,800 organizations, far more than one dropdown page. */}
      <SearchableSelect
        label="Organization"
        value={formData.organization_id}
        onChange={(v) => handleChange("organization_id", v)}
        search={searchOrganizationOptions}
        resolveLabel={organizationLabel}
        queryKeyBase="contact-form-organization"
        emptyLabel="No organization"
        placeholder="Search organizations..."
        testId="contact-organization"
      />

      <Textarea
        label="Description"
        value={formData.description}
        onChange={(e) => handleChange("description", e.target.value)}
        rows={3}
      />

      <div className="space-y-4">
        <h3 className="text-sm font-medium text-gray-700">Address</h3>
        <div className="grid grid-cols-1 gap-4">
          <Input
            label="Address Line 1"
            value={formData.address_line1}
            onChange={(e) => handleChange("address_line1", e.target.value)}
          />

          <Input
            label="Address Line 2"
            value={formData.address_line2}
            onChange={(e) => handleChange("address_line2", e.target.value)}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="City"
              value={formData.city}
              onChange={(e) => handleChange("city", e.target.value)}
            />

            <Select
              label="State"
              value={formData.state}
              onChange={(e) => handleChange("state", e.target.value)}
            >
              <option value="">-- Select state --</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </Select>

            <Input
              label="Postal Code"
              value={formData.postal_code}
              onChange={(e) => handleChange("postal_code", e.target.value)}
            />
          </div>

          <Input
            label="Country"
            value={formData.country}
            onChange={(e) => handleChange("country", e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading
            ? "Saving..."
            : isEditing
              ? "Update Contact"
              : "Create Contact"}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
